// Writing and editing a recipe by hand (components/recipes/RecipeEditor.tsx):
// the pure half. A draft is what the form holds — every ingredient a row like a
// list item: its name, its amount as typed ("500 g") and its category — and
// turning it back into a recipe reads the amount with the same parser as the
// link importer (lib/recipe.ts), so a hand-written recipe scales, merges and
// matches the list exactly like an imported one.

import type { Recipe, RecipeIngredient, RecipeStep } from './supabase'
import { displayAmount, parseIngredient } from './recipe'
import { stepMinutes } from './stepMinutes'

export interface DraftIngredient {
  /** Stable React key; never saved. */
  key: string
  name: string
  /** As typed: "500 g", "2", "1 nypa"; '' = none. */
  amount: string
  /** What the row shows: the member's pick, else the household's guess. */
  categoryId: string | null
  /** A member chose it: saved, and no longer re-guessed as the name changes. */
  categoryPicked: boolean
  pantry: boolean
}

export interface DraftStep {
  key: string
  text: string
}

export interface RecipeDraft {
  title: string
  /** As typed: '' = not given. */
  servings: string
  minutes: string
  ingredients: DraftIngredient[]
  steps: DraftStep[]
}

export type RecipeFields = Pick<Recipe, 'title' | 'servings' | 'minutes' | 'ingredients' | 'steps'>

// The database's bounds (030 baseline, 033) and the importer's, so a hand-made
// recipe can't be one the rest of the app was never built for.
export const LIMITS = {
  title: 120,
  servings: 12,
  minutes: 1440,
  ingredients: 80,
  ingredientLine: 160,
  steps: 60,
  stepText: 1000,
} as const

let seq = 0
const nextKey = () => `d${++seq}`

/** One ingredient as a line of text: "500 g fast potatis", "1½ bunches basil", "salt". */
export function ingredientLine(g: Pick<RecipeIngredient, 'name' | 'amount' | 'unit'>, locale?: string): string {
  return [displayAmount(g.amount, g.unit, locale), g.name].filter(Boolean).join(' ')
}

export function emptyDraft(): RecipeDraft {
  return { title: '', servings: '4', minutes: '', ingredients: [blankIngredient()], steps: [blankStep()] }
}

export const blankIngredient = (): DraftIngredient =>
  ({ key: nextKey(), name: '', amount: '', categoryId: null, categoryPicked: false, pantry: false })
export const blankStep = (): DraftStep => ({ key: nextKey(), text: '' })

/**
 * The recipe as a form. `categoryOf` is where each ingredient goes now
 * (lib/recipeBook.ts `ingredientCategory`); without it only picked ones show.
 */
export function draftFromRecipe(
  r: Pick<Recipe, 'title' | 'servings' | 'minutes' | 'ingredients' | 'steps'>,
  locale?: string,
  categoryOf: (g: RecipeIngredient) => string | null = g => g.category_id ?? null,
): RecipeDraft {
  return {
    title: r.title,
    servings: r.servings == null ? '' : String(r.servings),
    minutes: r.minutes == null ? '' : String(r.minutes),
    ingredients: r.ingredients.map(g => ({
      key: nextKey(),
      name: g.name,
      amount: displayAmount(g.amount, g.unit, locale),
      categoryId: categoryOf(g),
      categoryPicked: g.category_id != null,
      pantry: g.pantry === true,
    })),
    steps: r.steps.map(s => ({ key: nextKey(), text: s.text })),
  }
}

/** A pasted line ("500 g fast potatis") as a row's name and amount. */
export function splitLine(line: string, locale?: string): { name: string; amount: string } {
  const p = parseIngredient(line)
  if (p.amount == null) return { name: tidy(line), amount: '' }
  return { name: tidy(p.name) || tidy(line), amount: displayAmount(p.amount, p.unit, locale) }
}

const tidy = (s: string) => s.replace(/\s+/g, ' ').trim()

/**
 * An amount typed on its own ("500 g", "2", "1 nypa"): its number and unit, and
 * any words it couldn't read ("lite", the "stora" of "2 stora") to go back in
 * front of the name rather than be lost.
 */
export function readAmount(text: string): { amount: number | null; unit: string | null; rest: string } {
  const t = tidy(text)
  if (!t) return { amount: null, unit: null, rest: '' }
  const p = parseIngredient(t)
  if (p.amount == null) return { amount: null, unit: null, rest: t }
  return { amount: p.amount, unit: p.unit, rest: tidy(p.name) }
}

/**
 * One row as a saved ingredient. The amount field is read on its own; what it
 * can't read ("lite", the "stora" of "2 stora") goes back in front of the name
 * rather than being lost. A row with no amount whose name starts with one
 * ("500 g potatis", the old one-line habit) is split like an import.
 */
function ingredientFromRow(g: DraftIngredient): RecipeIngredient | null {
  let name = tidy(g.name)
  const amountText = tidy(g.amount)
  let amount: number | null = null
  let unit: string | null = null
  if (amountText) {
    const read = readAmount(amountText)
    amount = read.amount
    unit = read.unit
    name = tidy(`${read.rest} ${name}`)
  } else if (name) {
    const p = parseIngredient(name)
    if (p.amount != null) { amount = p.amount; unit = p.unit; name = tidy(p.name) || name }
  }
  name = name.slice(0, LIMITS.ingredientLine)
  if (!name) return null
  return { name, amount, unit, ...(g.pantry ? { pantry: true } : {}), ...(g.categoryPicked && g.categoryId ? { category_id: g.categoryId } : {}) }
}

export type DraftErrors = Partial<Record<'title' | 'servings' | 'minutes' | 'ingredients' | 'steps', string>>
export type DraftResult = { ok: true; fields: RecipeFields } | { ok: false; errors: DraftErrors }

function wholeNumber(raw: string, max: number): number | null | 'bad' {
  const t = raw.trim()
  if (!t) return null
  if (!/^\d{1,4}$/.test(t)) return 'bad'
  const n = Number(t)
  return n >= 1 && n <= max ? n : 'bad'
}

/** The draft as a recipe to save, or what to fix first. Empty lines are dropped. */
export function recipeFromDraft(d: RecipeDraft): DraftResult {
  const errors: DraftErrors = {}
  const title = d.title.trim()
  if (!title) errors.title = 'Give the recipe a name.'
  else if (title.length > LIMITS.title) errors.title = `Keep the name under ${LIMITS.title} characters.`

  const servings = wholeNumber(d.servings, LIMITS.servings)
  if (servings === 'bad') errors.servings = `Servings is a number from 1 to ${LIMITS.servings}.`
  const minutes = wholeNumber(d.minutes, LIMITS.minutes)
  if (minutes === 'bad') errors.minutes = `Time is minutes, from 1 to ${LIMITS.minutes}.`

  const ingredients: RecipeIngredient[] = []
  for (const g of d.ingredients) {
    const ingredient = ingredientFromRow(g)
    if (ingredient) ingredients.push(ingredient)
  }
  if (ingredients.length === 0) errors.ingredients = 'Add at least one ingredient.'
  else if (ingredients.length > LIMITS.ingredients) errors.ingredients = `Up to ${LIMITS.ingredients} ingredients.`

  const steps: RecipeStep[] = d.steps
    .map(s => s.text.trim().slice(0, LIMITS.stepText))
    .filter(Boolean)
    .map(text => ({ text, minutes: stepMinutes(text) }))
  if (steps.length > LIMITS.steps) errors.steps = `Up to ${LIMITS.steps} steps.`

  if (Object.keys(errors).length) return { ok: false, errors }
  return {
    ok: true,
    fields: {
      title,
      servings: servings as number | null,
      minutes: minutes as number | null,
      ingredients,
      steps,
    },
  }
}

/** Moves one entry of a list up (-1) or down (+1); out of range leaves it as is. */
export function move<T>(list: T[], index: number, by: -1 | 1): T[] {
  const to = index + by
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list
  const next = [...list]
  const [it] = next.splice(index, 1)
  next.splice(to, 0, it)
  return next
}
